import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Building2, Users, Handshake, ArrowRight, Globe, Award, Target } from "lucide-react";
import { partnerships } from "../mock";

const PartnershipsSection = () => {
  return (
    <section id="partnerships" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-6">
            <Handshake className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Strategic Partnerships
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Collaborating with organizations dedicated to women empowerment and child development across Gujarat
          </p>
        </div>

        {/* Partnership Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <Card className="text-center group hover:shadow-lg transition-all duration-300">
            <CardHeader>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">15+</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">Development Organizations</p>
            </CardContent>
          </Card>
          
          <Card className="text-center group hover:shadow-lg transition-all duration-300">
            <CardHeader>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">₹8,500Cr</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">WCD Budget Allocation</p>
            </CardContent>
          </Card>
          
          <Card className="text-center group hover:shadow-lg transition-all duration-300">
            <CardHeader>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">1.06L+</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">Anganwadi Workers</p>
            </CardContent>
          </Card>
        </div>

        {/* Partners Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Our Development Partners
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {partnerships.map((partner, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-pink-50 transition-colors">
                    <div className="w-12 h-8 bg-gray-300 rounded flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-600">
                        {partner.name.substring(0, 3)}
                      </span>
                    </div>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">
                    {partner.name}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {partner.focus}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Partnership Types */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <Card className="group hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <div className="flex items-center mb-4">
                <Globe className="w-6 h-6 text-orange-500 mr-3" />
                <CardTitle className="text-xl text-gray-900 group-hover:text-orange-600 transition-colors">
                  Global Tech Giants
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Strategic alliances with leading technology companies for AI infrastructure, cloud services, and advanced analytics platforms.
              </p>
              <Badge className="bg-orange-100 text-orange-700">
                Infrastructure & Platform
              </Badge>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <div className="flex items-center mb-4">
                <Award className="w-6 h-6 text-blue-500 mr-3" />
                <CardTitle className="text-xl text-gray-900 group-hover:text-blue-600 transition-colors">
                  Research Institutions
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Collaborations with leading universities and research centers for AI innovation, talent development, and cutting-edge research.
              </p>
              <Badge className="bg-blue-100 text-blue-700">
                Research & Development
              </Badge>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <div className="flex items-center mb-4">
                <Users className="w-6 h-6 text-green-500 mr-3" />
                <CardTitle className="text-xl text-gray-900 group-hover:text-green-600 transition-colors">
                  Startup Ecosystem
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Support for local startups and innovation hubs to foster entrepreneurship and develop AI solutions for governance challenges.
              </p>
              <Badge className="bg-green-100 text-green-700">
                Innovation & Startups
              </Badge>
            </CardContent>
          </Card>
        </div>

        {/* Partnership CTA */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-4">
              Partner with Gujarat WCD Initiative
            </h3>
            <p className="text-gray-300 mb-8 text-lg">
              Join us in empowering women and ensuring child development across Gujarat. Explore partnership opportunities in nutrition, technology, training, and community development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold px-8"
              >
                Become a Partner
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
              >
                Download WCD Partnership Guide
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-8 text-gray-400 text-sm">
              <span>✓ Government Support</span>
              <span>✓ Tax Incentives</span>
              <span>✓ Skilled Workforce</span>
              <span>✓ Market Access</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipsSection;